import {
  Drawer,
  List,
  Divider,
  Box,
  Typography,
  Avatar,
  IconButton,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import React, { useState } from "react";
import { LogoutButton } from "../Buttons/LogoutButton";
import DashboardIcon from "@mui/icons-material/Dashboard";
import StoreIcon from "@mui/icons-material/Store";
import CampaignIcon from "@mui/icons-material/Campaign";
import CloseIcon from "@mui/icons-material/Close";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import SettingsIcon from "@mui/icons-material/Settings";
import LinkIcon from "@mui/icons-material/Link";
import TuneIcon from "@mui/icons-material/Tune";
import SidebarItem from "../Sidebar/SidebarItem";
import SmallSidebarItem from "../Sidebar/SmallSidebarItem";
import BrandSidebarItem from "../Sidebar/BrandSidebarItem";
import useTimeoutedEffect from "../../hooks/useTimeoutEffect";
import { apiGetBrandsList } from "../../services/brand.api";
import { useAuthStore } from "../../store/useAuthStore";
import { useCan, useScope, billing, Can } from "../../auth";
import Logo from "../../assets/images/logo.svg?react";
import type { BrandSelectHandler, SelectedBrand, SidebarBrand } from "../Sidebar";

interface MenuContainerProps {
  open: boolean;
  handleClose: () => void;
  anchorEl: HTMLElement | null;
}

const ROOT_ADMIN_ROUTES = ["/brands"];
const BRAND_ADMIN_ROUTES = ["/brand-config"];
const COMMON_ROUTES = ["/dashboard", "/connections", "/campaigns"];

const GLOBAL_BRAND: SidebarBrand = {
  id: null,
  name: "All Brands",
  domain: "",
  logo_url: null,
  children: [],
};


const MenuContainer: React.FC<MenuContainerProps> = ({
  open,
  handleClose,
}) => {
  const nav = useNavigate();
  const location = useLocation();
  const { userProfile } = useAuthStore();
  const can = useCan();
  const { isBrandScoped, isGlobalScope } = useScope();
  const isDashboardRoute = location.pathname.includes("/dashboard");

  const [adminBrand, setAdminBrand] = useState<SidebarBrand | null>(null);
  const [childBrands, setChildBrands] = useState<SidebarBrand[] | null>(null);
  const [selectedBrand, setSelectedBrand] = useState<SelectedBrand>({ brandId: null });

  useTimeoutedEffect(async () => {
    const brands = await apiGetBrandsList();
    if (!brands || !brands.length) return;

    const newAdminBrand: SidebarBrand = isGlobalScope ? GLOBAL_BRAND : {
      id: brands[0].id,
      name: brands[0].name,
      domain: brands[0].domain,
      logo_url: brands[0].logo_url,
      children: brands[0].children || [],
    };

    setAdminBrand(newAdminBrand);
    setChildBrands(
      isGlobalScope
        ? brands.map((b) => ({
          id: b.id,
          name: b.name,
          domain: b.domain,
          logo_url: b.logo_url,
          children: b.children || [],
        }))
        : (brands[0]?.children || []).map((b) => ({
          id: b.id,
          name: b.name,
          domain: b.domain,
          logo_url: b.logo_url,
          children: b.children || [],
        })),
    );
    setSelectedBrand({
      brandId: newAdminBrand.id,
      showChildrenStatistics: !!newAdminBrand.children.length,
    });
  }, [isGlobalScope]);

  const handleBrandSelect: BrandSelectHandler = (
    brandId: string | null,
    showChildrenStatistics: boolean = false,
  ) => {
    setSelectedBrand({ brandId, showChildrenStatistics });
    nav(
      `/dashboard${brandId
        ? `/${brandId}?showChildrenStatistics=${showChildrenStatistics}`
        : ""
      }`,
    );
    handleClose();
  };

  const allMenuItems = [
    {
      label: "Dashboard",
      route: "/dashboard",
      icon: <DashboardIcon />,
    },
    {
      label: "Connections",
      route: "/connections",
      icon: <LinkIcon />,
    },
    {
      label: "Brands",
      route: "/brands",
      icon: <StoreIcon />,
    },
    {
      label: "Campaigns",
      route: "/campaigns",
      icon: <CampaignIcon />,
    },
    {
      label: "Brand Configuration",
      route: "/brand-config",
      icon: <TuneIcon />,
    },
  ];

  // Filter menu items based on user role (same as Sidebar)
  const menuItems = allMenuItems.filter((item) => {
    return [
      ...COMMON_ROUTES,
      ...(isGlobalScope ? ROOT_ADMIN_ROUTES : []),
      ...(isBrandScoped ? BRAND_ADMIN_ROUTES : []),
    ].includes(item.route || "");
  });

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: {
          sx: {
            width: "280px",
            bgcolor: "#FFFFFF",
          },
        },
      }}
    >
      {/* Header with Logo and Close */}
      <Box
        sx={{
          p: 2,
          borderBottom: "1px solid #E5E7EB",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Logo style={{ height: "32px", width: "auto" }} />
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* User Info */}
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #E5E7EB",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Avatar src={""} alt="User Avatar" sx={{ width: 40, height: 40 }} />
          <Box sx={{ ml: 2 }}>
            <Typography variant="body1" fontWeight={600}>
              {userProfile?.email || "User"}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {isGlobalScope ? "Root Admin" : "Brand Admin"}
            </Typography>
          </Box>
        </Box>
        <LogoutButton />
      </Box>

      {/* Navigation Items */}
      <Box sx={{ flex: 1, overflowY: "auto", py: 2 }}>
        <List sx={{ p: 0 }}>
          {menuItems.map((item) => {
            const { label, icon, route } = item;
            const isSelected = route ? location.pathname.includes(route) : false;
            const isDashboard = route === "/dashboard";
            const showBrandItems = isDashboardRoute && isDashboard && adminBrand;

            const handleClick = () => {
              if (!route) return;
              if (isDashboard && (isBrandScoped || isGlobalScope)) {
                setSelectedBrand({ brandId: null });
              }
              nav(route);
              handleClose();
            };

            return (
              <React.Fragment key={label}>
                <SidebarItem
                  label={label}
                  icon={icon}
                  selected={isSelected}
                  expanded={true}
                  onClick={handleClick}
                />
                {showBrandItems && adminBrand && (
                  <BrandSidebarItem
                    brand={adminBrand}
                    childBrands={childBrands || []}
                    selectedBrand={selectedBrand}
                    expanded={true}
                    onBrandSelect={handleBrandSelect}
                  />
                )}
              </React.Fragment>
            );
          })}
        </List>

        <Divider sx={{ my: 2, mx: 2 }} />

        <List sx={{ p: 0 }}>
          <Can perm={billing.read.GetBillingData}>
            <SmallSidebarItem
              label="Billing"
              icon={<CreditCardIcon />}
              expanded={true}
              route="/billing"
              onClose={handleClose}
            />
          </Can>
          <SmallSidebarItem
            label="Set up your Trust ID"
            icon={<VerifiedUserIcon />}
            expanded={true}
            route="/trustid-setup"
            onClose={handleClose}
          />
          <SmallSidebarItem
            label="Account Settings"
            icon={<SettingsIcon />}
            expanded={true}
            route="/account"
            onClose={handleClose}
          />
        </List>
      </Box>
    </Drawer>
  );
};

export default MenuContainer;
